import React, { Fragment, useState } from "react";
import { getVal, LoaderOverlay, ModalSmall, UI_BODY, UI_CAPTION, useBannerContext } from "oolib";
import { useBulkQuickUpdateContent } from "../../../../pageTpls/ContributeTpl/queryHooks/useBulkQuickUpdateContent";
import { useBulkDeleteContent } from "../../../../pageTpls/ContributeTpl/queryHooks/useBulkDeleteContent";
import { useTriggerAIAutoAnnotate } from "../../../../utils/react-query-hooks/aiAnnotation";
import { useActivateSelfServeSurveyModal } from "../../../../pageTpls/PlatformBuilder/subModules/ContentTypesManager/comps/ContentTypeLanding/PublishTypeLanding/useActivateSelfServeSurveyModal";
import { useSignalPicker } from "../../../SignalPickerModal";

export const useGetBulkActionsConfig = ({ contentType, tableConfig }) => {
   const { mutate: bulkQuickUpdateContents, status: bulkQuickUpdateStatus } = useBulkQuickUpdateContent();
   const { mutate: bulkDeleteContents, status: bulkDeleteStatus } = useBulkDeleteContent();
   const { mutate: triggerAIAutoAnnotate, status: aiAnnotateStatus } = useTriggerAIAutoAnnotate();
   // Off-prod Signals-picker gate on AI Annotate. On prod requestSignalPicker
   // invokes the callback immediately (no modal); off-prod it opens the modal
   // and passes back the picked allow-list.
   const { requestPickerThenRun: requestSignalPicker, SignalPickerModalNode } = useSignalPicker();
   const { SET_INFO_BANNER } = useBannerContext();

   const [bulkActions_selectedRowIds, bulkActions_setSelectedRowIds] = useState([]);
   // Holds { contentType, contentIds } when user clicks Delete and the confirm
   // modal is open. Cleared on close / after successful delete.
   const [pendingDeleteConfirm, setPendingDeleteConfirm] = useState(null);

   const { ActivateSelfServeSurveyModal, activateSelfServeSurvey, activateSelfServeSurveyContentTypes } = useActivateSelfServeSurveyModal({
      contentType: contentType,
      onConfirmFunction: "bulkActivateSelfServeSurvey",
   });

   const handleShortlistUnshortlist = ({ data, shortlist = true }) => {
      if(bulkQuickUpdateStatus === "loading") return;

      const contentType = data?.[0]?.meta.kp_content_type;
      const filteredContentIds = bulkActions_selectedRowIds.filter((contentId) => {
         const matchingDataObj = data.find((obj) => obj._id === contentId);
         const isShortlisted = matchingDataObj.main.shortlistForSurvey2;
         return shortlist ? !isShortlisted : isShortlisted;
      });

      bulkQuickUpdateContents({
         contentType: contentType,
         body: {
            contentIds: filteredContentIds,
            queryObj: {
               $set: {
                  "main.shortlistForSurvey2": shortlist,
               },
            },
         }
      }, {
         onSuccess: () => {
            SET_INFO_BANNER(
               shortlist ? "Shortlisted successfully" : "Unshortlisted successfully",
               "green",
               3000
            );
            bulkActions_setSelectedRowIds([]);
         },
      });
   };

   const handleBulkActivateSelfServeSurvey = ({ data }) => {
      // getting userIds from selected data objects(filtering only selected contents first)
      let userIds = data
         .filter((obj) => bulkActions_selectedRowIds.includes(getVal(obj, tableConfig.rowIdPath)))
         .map((selectedObj) => selectedObj.meta.kp_contributed_by?._id)
         .filter(Boolean);

      userIds = [...(new Set(userIds))];
      activateSelfServeSurvey(null, { userIds, bulkActions_setSelectedRowIds });
   }

   const handleAIAnnotate = ({ data }) => {
      if (aiAnnotateStatus === "loading") return;
      if (bulkActions_selectedRowIds.length === 0) return;

      const contentType = data?.[0]?.meta?.kp_content_type;
      if (!contentType) return;

      requestSignalPicker((runOptions) => {
         triggerAIAutoAnnotate({
            contentType,
            docIds: bulkActions_selectedRowIds,
            ...(runOptions || {}),
         }, {
            onSuccess: (res) => {
               // A 2xx is not a queue confirmation: the BE answers
               // "alreadyRunning" when every selected document is still being
               // annotated by an earlier run. Documents in flight are skipped
               // per document, so a partial overlap still queues the rest.
               if (res?.data?.status === "alreadyRunning") {
                  SET_INFO_BANNER(res?.data?.message || "These documents are already being annotated.", "yellow", 5000);
                  return;
               }
               const summary = res?.data?.summary;
               const queued = summary?.docsToProcess ?? summary?.jobsQueued;
               const skipped = summary?.skippedInFlightCount || 0;
               const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
               SET_INFO_BANNER(
                  (queued ? `Annotation queued for ${plural(queued, "document")}.` : `Annotation queued for ${plural(bulkActions_selectedRowIds.length, "document")}.`) +
                     (skipped ? ` ${plural(skipped, "document")} already being annotated ${skipped === 1 ? "was" : "were"} skipped.` : ""),
                  "green",
                  skipped ? 6000 : 4000
               );
               bulkActions_setSelectedRowIds([]);
            },
         });
      });
   };

   const handleBulkDelete = ({ data }) => {
      if (bulkActions_selectedRowIds.length === 0) return;
      const contentType = data?.[0]?.meta?.kp_content_type;
      if (!contentType) return;

      // open the confirm modal — actual delete fires from the modal's onConfirm
      setPendingDeleteConfirm({
         contentType,
         contentIds: [...bulkActions_selectedRowIds],
      });
   };

   const performBulkDelete = () => {
      if (!pendingDeleteConfirm || bulkDeleteStatus === "loading") return;
      const { contentType, contentIds } = pendingDeleteConfirm;

      // Close the modal right away — progress is shown via LoaderOverlay,
      // which is gated on bulkDeleteStatus === "loading".
      setPendingDeleteConfirm(null);

      bulkDeleteContents({ contentType, contentIds }, {
         onSuccess: (res) => {
            const { success: succeeded = 0, failed = 0 } = res?.data || {};
            const color = failed && succeeded ? "yellow" : failed ? "red" : "green";
            const msg = failed
               ? `Deleted ${succeeded} of ${contentIds.length}. ${failed} failed.`
               : `Deleted ${succeeded} document${succeeded > 1 ? "s" : ""}.`;
            SET_INFO_BANNER(msg, color, 4000);
            bulkActions_setSelectedRowIds([]);
         },
         // banner on error is set by the hook's onError (genErrorMsgBanners)
      });
   };

   const ConfirmBulkDeleteModal = pendingDeleteConfirm ? (
      <ModalSmall
         title={`Delete ${pendingDeleteConfirm.contentIds.length} document${pendingDeleteConfirm.contentIds.length > 1 ? "s" : ""}?`}
         onClose={() => setPendingDeleteConfirm(null)}
         onConfirmText="Delete"
         showActionPanel
         onConfirm={performBulkDelete}
      >
         <UI_BODY>
            This action cannot be undone. The selected document
            {pendingDeleteConfirm.contentIds.length > 1 ? "s" : ""} will be permanently removed.
         </UI_BODY>
         {/* Lazy-load disclosure: "Select All" only picks rows the user has
             already scrolled into view. Without this nudge it's easy to
             confirm a partial delete thinking it covered the whole dataset. */}
         <UI_CAPTION
            color="greyColor60"
            style={{ display: "block", marginTop: "0.75rem" }}
         >
            Note: "Select All" only selects rows that are currently loaded in
            the table. To act on more rows, scroll to load them first or
            apply a filter to narrow the set.
         </UI_CAPTION>
      </ModalSmall>
   ) : null;

   const BulkDeleteLoaderOverlay = bulkDeleteStatus === "loading" ? (
      <LoaderOverlay loaderText="Deleting..." />
   ) : null;

   const AIAnnotateLoaderOverlay = aiAnnotateStatus === "loading" ? (
      <LoaderOverlay loaderText="Queueing annotations..." />
   ) : null;

   const bulkActionsConfig = {
      shortlist: ({ data }) => {
         handleShortlistUnshortlist({ data, shortlist: true });
      },
      unshortlist: ({ data }) => {
         handleShortlistUnshortlist({ data, shortlist: false });
      },
      activateSelfServeSurvey: ({ data }) => {
         handleBulkActivateSelfServeSurvey({ data });
      },
      aiAnnotate: ({ data }) => {
         handleAIAnnotate({ data });
      },
      bulkDelete: ({ data }) => {
         handleBulkDelete({ data });
      },
   };

   const BulkActionModals = (
      <Fragment>
         {ActivateSelfServeSurveyModal}
         {ConfirmBulkDeleteModal}
         {BulkDeleteLoaderOverlay}
         {AIAnnotateLoaderOverlay}
         {SignalPickerModalNode}
      </Fragment>
   );

   return {
      bulkActions_selectedRowIds,
      bulkActions_setSelectedRowIds,
      bulkActionsConfig,
      // Back-compat: TableElem currently renders {ActivateSelfServeSurveyModal}.
      // BulkActionModals is the new umbrella node — prefer it going forward.
      ActivateSelfServeSurveyModal: BulkActionModals,
   };
}
